import React, { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@ui/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@ui/components/ui/dialog';

interface RemoveProductDialogProps {
  open: boolean;
  name: string;
  isPending: boolean;
  /** Set when the server refused, which it does the moment somebody has filed time against it. */
  failed?: string | null;
  onCancel: () => void;
  onConfirm: () => void;
}

/**
 * Removal is permanent and says so: what goes with the product is everything that only described it. The
 * refusal is shown in here rather than as a toast, because this is the dialog that asked the question.
 */
const RemoveProductDialog = ({
  open,
  name,
  isPending,
  failed,
  onCancel,
  onConfirm,
}: RemoveProductDialogProps): ReactElement => {
  const { t } = useTranslation();

  return (
    <Dialog open={open} onOpenChange={isOpen => !isOpen && onCancel()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('removeProduct.title', { name })}</DialogTitle>
        </DialogHeader>

        <p className="text-sm text-foreground">{t('removeProduct.body')}</p>
        {failed ? <p className="text-sm text-destructive">{failed}</p> : null}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onCancel} disabled={isPending}>
            {t('actions.cancel')}
          </Button>
          <Button type="button" onClick={onConfirm} disabled={isPending}>
            {t('removeProduct.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RemoveProductDialog;
